type ApiCacheEntry = {
  key: string;
  operation: string;
  value: unknown;
  updatedAt: number;
  expiresAt: number;
};

const DB_NAME = "lobbyze_http_cache";
const DB_VERSION = 1;
const STORE_CACHE = "api_cache_entries";

const DEFAULT_TTL_MS = 60 * 60 * 1000;
const DEFAULT_MAX_ENTRIES_PER_OPERATION = 4;
const DEFAULT_MAX_GLOBAL_ENTRIES = 80;
const PRUNE_INTERVAL_MS = 60 * 1000;
const MAX_ENTRY_BYTES = 350 * 1024;
const MAX_TOTAL_BYTES = 30 * 1024 * 1024;
const QUOTA_EVICTION_BATCH = 20;

const HIGH_CARDINALITY_OPERATIONS = new Set<string>([
  "getTournamentFilter",
  "listOnTimeTournaments",
  "getOrganizedSchedules",
  "getRebalanceList",
  "listBlinds",
  "getBlindByIds",
  "getStats",
  "getStatsForDay",
]);

function simpleHash(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        const store = db.createObjectStore(STORE_CACHE, { keyPath: "key" });
        store.createIndex("operation", "operation", { unique: false });
        store.createIndex("updatedAt", "updatedAt", { unique: false });
        store.createIndex("operation_updatedAt", ["operation", "updatedAt"], { unique: false });
      }
    };
  });
}

export class ApiCacheStorage {
  private static db: IDBDatabase | null = null;
  private static openPromise: Promise<IDBDatabase> | null = null;
  private static memory = new Map<string, ApiCacheEntry>();
  private static lastPruneAt = 0;
  private static prunePromise: Promise<void> | null = null;

  private static async getDb(): Promise<IDBDatabase> {
    if (this.db) return this.db;
    if (!this.openPromise) this.openPromise = openDb();
    this.db = await this.openPromise;
    this.scheduleMaintenance(true);
    return this.db;
  }

  private static scheduleMaintenance(force = false): void {
    if (this.prunePromise) {
      return;
    }

    const now = Date.now();
    if (!force && now - this.lastPruneAt < PRUNE_INTERVAL_MS) {
      return;
    }

    this.lastPruneAt = now;
    this.prunePromise = this.runMaintenance().finally(() => {
      this.prunePromise = null;
    });
  }

  private static async runMaintenance(): Promise<void> {
    try {
      const db = await this.getDb();
      await this.pruneExpired(db);
      await this.pruneGlobal(db, DEFAULT_MAX_GLOBAL_ENTRIES);
      await this.pruneGlobalBytes(db, MAX_TOTAL_BYTES);
    } catch {
      // no-op
    }
  }

  static normalizeKey(operation: string, cacheKey?: string): string {
    if (!cacheKey) {
      return operation;
    }

    const compact = cacheKey
      .replace(/_\{.*\}$/s, "")
      .replace(/_\[.*\]$/s, "")
      .replace(/timezone[:=]\s*-?\d+/gi, "timezone")
      .trim();

    if (!compact || compact.length > 180) {
      return `${operation}:${simpleHash(cacheKey)}`;
    }

    return compact;
  }

  static ttlForOperation(operation: string): number {
    void operation;
    return DEFAULT_TTL_MS;
  }

  static maxEntriesForOperation(operation: string): number {
    if (HIGH_CARDINALITY_OPERATIONS.has(operation)) {
      return 1;
    }
    return DEFAULT_MAX_ENTRIES_PER_OPERATION;
  }

  static async get<T>(key: string): Promise<T | null> {
    this.scheduleMaintenance();
    const now = Date.now();
    const mem = this.memory.get(key);
    if (mem) {
      if (mem.expiresAt > now) {
        return mem.value as T;
      }
      this.memory.delete(key);
    }

    try {
      const db = await this.getDb();
      const tx = db.transaction([STORE_CACHE], "readonly");
      const store = tx.objectStore(STORE_CACHE);

      const entry = await new Promise<ApiCacheEntry | undefined>((resolve, reject) => {
        const req = store.get(key);
        req.onsuccess = () => resolve(req.result as ApiCacheEntry | undefined);
        req.onerror = () => reject(req.error);
      });

      if (!entry) {
        return null;
      }

      if (entry.expiresAt <= now) {
        void this.remove(key);
        return null;
      }

      this.memory.set(key, entry);
      return entry.value as T;
    } catch {
      return null;
    }
  }

  static async set<T>(
    key: string,
    operation: string,
    value: T,
    ttlMs = DEFAULT_TTL_MS
  ): Promise<void> {
    this.scheduleMaintenance();
    if (!this.canPersistValue(value)) {
      return;
    }

    const now = Date.now();
    const entry: ApiCacheEntry = {
      key,
      operation,
      value,
      updatedAt: now,
      expiresAt: now + ttlMs,
    };

    this.memory.set(key, entry);

    try {
      const db = await this.getDb();
      await this.persistEntry(db, entry);
      // Keep write-path lightweight: only operation-local pruning here.
      await this.pruneByOperation(db, operation, this.maxEntriesForOperation(operation));
    } catch (error) {
      if (this.isQuotaError(error)) {
        try {
          const db = await this.getDb();
          await this.pruneOldest(db, QUOTA_EVICTION_BATCH);
          await this.persistEntry(db, entry);
          await this.pruneByOperation(db, operation, this.maxEntriesForOperation(operation));
          return;
        } catch {
          this.memory.delete(key);
          return;
        }
      }
      this.memory.delete(key);
      // no-op on cache persistence failure
    }
  }

  static async remove(key: string): Promise<void> {
    this.memory.delete(key);

    try {
      const db = await this.getDb();
      const tx = db.transaction([STORE_CACHE], "readwrite");
      tx.objectStore(STORE_CACHE).delete(key);
    } catch {
      // no-op
    }
  }

  static async clear(): Promise<void> {
    this.memory.clear();
    try {
      const db = await this.getDb();
      const tx = db.transaction([STORE_CACHE], "readwrite");
      tx.objectStore(STORE_CACHE).clear();
    } catch {
      // no-op
    }
  }

  private static async persistEntry(db: IDBDatabase, entry: ApiCacheEntry): Promise<void> {
    const tx = db.transaction([STORE_CACHE], "readwrite");
    const store = tx.objectStore(STORE_CACHE);
    store.put(entry);

    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private static canPersistValue(value: unknown): boolean {
    try {
      const serialized = JSON.stringify(value);
      if (!serialized) {
        return true;
      }
      return serialized.length <= MAX_ENTRY_BYTES;
    } catch {
      return false;
    }
  }

  private static isQuotaError(error: unknown): boolean {
    const e = error as { name?: string; message?: string } | null;
    const name = e?.name ?? "";
    const message = e?.message ?? "";
    return name === "QuotaExceededError" || /quota/i.test(message);
  }

  private static async getAllByOperation(db: IDBDatabase, operation: string): Promise<ApiCacheEntry[]> {
    const tx = db.transaction([STORE_CACHE], "readonly");
    const index = tx.objectStore(STORE_CACHE).index("operation");

    return new Promise((resolve, reject) => {
      const req = index.getAll(IDBKeyRange.only(operation));
      req.onsuccess = () => resolve((req.result as ApiCacheEntry[]) || []);
      req.onerror = () => reject(req.error);
    });
  }

  private static async getAllEntries(db: IDBDatabase): Promise<ApiCacheEntry[]> {
    const tx = db.transaction([STORE_CACHE], "readonly");
    const store = tx.objectStore(STORE_CACHE);

    return new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve((req.result as ApiCacheEntry[]) || []);
      req.onerror = () => reject(req.error);
    });
  }

  private static async deleteKeys(db: IDBDatabase, keys: string[]): Promise<void> {
    if (!keys.length) {
      return;
    }

    const tx = db.transaction([STORE_CACHE], "readwrite");
    const store = tx.objectStore(STORE_CACHE);

    for (const key of keys) {
      store.delete(key);
      this.memory.delete(key);
    }

    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private static async pruneByOperation(db: IDBDatabase, operation: string, limit: number): Promise<void> {
    const entries = await this.getAllByOperation(db, operation);
    if (entries.length <= limit) {
      return;
    }

    entries.sort((a, b) => b.updatedAt - a.updatedAt);
    const toDelete = entries.slice(limit).map((entry) => entry.key);
    await this.deleteKeys(db, toDelete);
  }

  private static async pruneGlobal(db: IDBDatabase, limit: number): Promise<void> {
    const entries = await this.getAllEntries(db);
    if (entries.length <= limit) {
      return;
    }

    entries.sort((a, b) => b.updatedAt - a.updatedAt);
    const toDelete = entries.slice(limit).map((entry) => entry.key);
    await this.deleteKeys(db, toDelete);
  }

  private static async pruneOldest(db: IDBDatabase, count: number): Promise<void> {
    if (count <= 0) {
      return;
    }

    const entries = await this.getAllEntries(db);
    if (!entries.length) {
      return;
    }

    entries.sort((a, b) => a.updatedAt - b.updatedAt);
    const toDelete = entries.slice(0, count).map((entry) => entry.key);
    await this.deleteKeys(db, toDelete);
  }

  private static estimateEntryBytes(entry: ApiCacheEntry): number {
    try {
      const payload = JSON.stringify(entry.value);
      const keyMeta = entry.key.length + entry.operation.length + 32;
      return (payload ? payload.length : 0) + keyMeta;
    } catch {
      return 0;
    }
  }

  private static async pruneGlobalBytes(db: IDBDatabase, limitBytes: number): Promise<void> {
    if (limitBytes <= 0) {
      return;
    }

    const entries = await this.getAllEntries(db);
    if (!entries.length) {
      return;
    }

    const sorted = [...entries].sort((a, b) => b.updatedAt - a.updatedAt);
    let used = 0;
    const toDelete: string[] = [];

    for (const entry of sorted) {
      const size = this.estimateEntryBytes(entry);
      if (used + size <= limitBytes) {
        used += size;
      } else {
        toDelete.push(entry.key);
      }
    }

    await this.deleteKeys(db, toDelete);
  }

  private static async pruneExpired(db: IDBDatabase): Promise<void> {
    const now = Date.now();
    const entries = await this.getAllEntries(db);
    const expiredKeys = entries
      .filter((entry) => entry.expiresAt <= now)
      .map((entry) => entry.key);

    await this.deleteKeys(db, expiredKeys);
  }
}
